import type { DraftRecording, Submission } from '@/types'
import { getPresignedUploadUrl, finalizeUpload } from '../api'
import { NonRetryableUploadError, withRetry } from './retry'

const UPLOAD_TIMEOUT_MS = 180_000

function isRetryableStatus(status: number): boolean {
  // Retry server errors, timeouts, and rate limits. Treat other 4xx as terminal.
  return status >= 500 || status === 408 || status === 429
}

function putBlob(
  url: string,
  blob: Blob,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', blob.type || 'audio/webm')
    xhr.timeout = UPLOAD_TIMEOUT_MS

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else if (isRetryableStatus(xhr.status)) {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`))
      } else {
        reject(
          new NonRetryableUploadError(`Upload failed: HTTP ${xhr.status}`, xhr.status),
        )
      }
    }

    xhr.onerror = () => reject(new Error('Upload failed: network error'))
    xhr.ontimeout = () => reject(new Error('Upload failed: timeout'))

    xhr.send(blob)
  })
}

export function uploadBlobWithRetry(
  url: string,
  blob: Blob,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return withRetry(() => putBlob(url, blob, onProgress))
}

export async function uploadDraft(
  draft: DraftRecording,
  options?: { onProgress?: (pct: number) => void },
): Promise<{ syncedAt: string; processingStatus: Submission['processingStatus'] }> {
  const { presignedUrl, objectKey } = await getPresignedUploadUrl(draft)

  await uploadBlobWithRetry(presignedUrl, draft.blob, options?.onProgress)

  const result = await finalizeUpload(draft, objectKey)

  return {
    syncedAt: result.syncedAt,
    processingStatus: result.processingStatus as Submission['processingStatus'],
  }
}
